import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Check, ChevronRight, Cpu, Printer, ScanLine } from 'lucide-react';
import { changeLaboratoryDeviceStatus, loadCollectionPrinters, loadLaboratoryDevices, printLaboratoryDeviceLabel } from './api';
import { Code128 } from './DeviceDrawings';
import { LabEmpty, LabFacts, LabResource, labError, labWhen } from './LaboratoryParts';
import { departmentName, itemLabel, stageName } from './LaboratoryText';
import { LabDrawer, LabInstrument } from './LaboratoryVisuals';
import { Head, Pill, SearchField, XomicsSelect } from './parts';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import './laboratory-devices.css';

const text = (key, values) => tx(`ui.lab.device.${key}`, values);
const STATUSES = ['in_service', 'out_of_service', 'not_used'];
const TONES = { in_service: 'ok', out_of_service: 'bad', not_used: 'flat' };

// Presentation only: the existing drawings where the model matches.
function drawing(device) {
  const name = `${device.brand} ${device.name}`.toLowerCase();
  if (name.includes('maxwell')) return 'maxwell_rsc';
  if (name.includes('qiasymphony')) return 'qiasymphony';
  if (name.includes('qubit')) return 'qubit';
  if (name.includes('hamilton')) return 'hamilton';
  if (name.includes('novaseq x')) return 'novaseq_x_series';
  if (name.includes('novaseq 6000')) return 'novaseq_6000';
  return null;
}

export function DeviceArt({ device }) {
  const code = drawing(device);
  return code ? <LabInstrument code={code} /> : <span className="xo-device-plain"><Cpu aria-hidden="true" /></span>;
}

function StatusChange({ device, onSaved }) {
  const [status, setStatus] = useState(device.status), [note, setNote] = useState('');
  const [busy, setBusy] = useState(false), [error, setError] = useState('');
  const inFlight = useRef(false);
  async function save() {
    if (inFlight.current) return;
    inFlight.current = true; setBusy(true); setError('');
    try { await changeLaboratoryDeviceStatus(device.id, status, note); setNote(''); await onSaved(); }
    catch (caught) { setError(labError(caught)); }
    finally { inFlight.current = false; setBusy(false); }
  }
  return <section className="xo-device-status-form">
    <h3>{text('change_status')}</h3>
    <label><span>{text('status_label')}</span><XomicsSelect value={status} onChange={event => setStatus(event.target.value)} disabled={busy}>
      {STATUSES.map(value => <option key={value} value={value}>{text(`status.${value}`)}</option>)}</XomicsSelect></label>
    <label><span>{text('note')}</span><textarea rows={3} maxLength={500} value={note} onChange={event => setNote(event.target.value)} disabled={busy} /></label>
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
    <button type="button" className="xo-btn" onClick={save} disabled={busy || note.trim() === '' || (status === device.status && note.trim() === '')}>{text(busy ? 'saving' : 'save')}</button>
  </section>;
}

// The machine's own label: its inventory number as a barcode. Scanned at a
// stage it chooses this machine, so nobody picks it from a list. It can be
// scanned off the screen, or printed on the laboratory's label printer.
function MachineLabel({ device, mayPrint }) {
  const [printers, setPrinters] = useState(null), [printer, setPrinter] = useState('');
  const [busy, setBusy] = useState(false), [error, setError] = useState(''), [printed, setPrinted] = useState(false);
  useEffect(() => {
    if (!mayPrint) return undefined;
    let current = true;
    loadCollectionPrinters().then((list) => { if (current) { setPrinters(list); if (list.length === 1) setPrinter(list[0].deviceId); } })
      .catch(() => { if (current) setPrinters([]); });
    return () => { current = false; };
  }, [mayPrint]);
  async function print() {
    if (busy || printer === '') return;
    setBusy(true); setError(''); setPrinted(false);
    try { await printLaboratoryDeviceLabel(device.id, printer); setPrinted(true); }
    catch (caught) { setError(labError(caught)); }
    finally { setBusy(false); }
  }
  return <section className="xo-device-label">
    <h3><ScanLine aria-hidden="true" />{tx('ui.lab.scan.label')}</h3>
    <div className="xo-device-label-code"><Code128 value={device.inventoryNumber} label={device.inventoryNumber} height={54} /><code>{device.inventoryNumber}</code></div>
    <p className="xo-lab-muted">{tx('ui.lab.scan.label_hint')}</p>
    {mayPrint && printers !== null && (printers.length === 0 ? <p className="xo-lab-muted">{tx('ui.lab.scan.no_printer')}</p> : <div className="xo-device-label-print">
      {printers.length > 1 && <XomicsSelect value={printer} onChange={event => setPrinter(event.target.value)} aria-label={tx('ui.lab.scan.label_printer')} disabled={busy}>
        <option value="">{tx('ui.lab.scan.label_printer')}</option>
        {printers.map(item => <option key={item.deviceId} value={item.deviceId}>{item.deviceId} · {item.type.model}</option>)}
      </XomicsSelect>}
      <button type="button" className="xo-btn-2" onClick={print} disabled={busy || printer === ''}><Printer aria-hidden="true" />{busy ? text('saving') : tx('ui.lab.scan.print_label')}</button>
      {printed && <span className="xo-scan-mark"><Check aria-hidden="true" />{tx('ui.lab.scan.label_printed')}</span>}
    </div>)}
    {error !== '' && <p className="xo-alert t-bad" role="alert">{error}</p>}
  </section>;
}

function DeviceDetails({ device, mayManage, onSaved, onClose }) {
  return <LabDrawer title={device.name} eyebrow={device.inventoryNumber} onClose={onClose}>
    <div className="xo-lab-drawer-instrument"><DeviceArt device={device} /></div>
    <div className="xo-lab-detail-tags"><Pill tone={TONES[device.status]}>{text(`status.${device.status}`)}</Pill></div>
    <MachineLabel key={device.id} device={device} mayPrint={mayManage} />
    <LabFacts entries={[
      [text('brand'), device.brand], [text('serial'), <code>{device.serialNumber}</code>], [text('department'), departmentName(device.department.code)],
      ...(device.statusChangedAt !== null ? [[text('status_changed'), `${labWhen(device.statusChangedAt)} · ${device.statusChangedBy}`]] : []),
      ...(device.statusNote ? [[text('status_note'), device.statusNote]] : []),
    ]} />
    <h3>{text('valid_stages')}</h3>
    {device.stages.length ? <ul className="xo-device-stages">{device.stages.map(stage => <li key={`${stage.workflowCode}-${stage.stageCode}`}>
      <strong>{stage.workflowCode}</strong><span>{stageName(stage.stageCode)}</span></li>)}</ul> : <p className="xo-lab-muted">{tx('ui.lab.machines.no_stages')}</p>}
    {device.methods.length > 0 && <p className="xo-lab-muted">{text('methods', { methods: device.methods.map(method => itemLabel(method)).join(', ') })}</p>}
    {mayManage && <StatusChange key={`${device.status}-${device.statusChangedAt}`} device={device} onSaved={onSaved} />}
  </LabDrawer>;
}

export default function XomicsLabDevices() {
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState('');
  const resource = useLiveResource(['laboratory'], loadLaboratoryDevices);
  return <div className="xo-platform-page xo-lab-page">
    <Head title={tx('ui.lab.machines.title')} subtitle={tx('ui.lab.machines.subtitle')} />
    <div className="xo-lab-catalogue-tools"><SearchField value={query} onChange={setQuery} placeholder={tx('ui.lab.machines.search')} /></div>
    <LabResource resource={resource}>{data => {
      const needle = query.trim().toLowerCase();
      const devices = data.devices.filter(device => `${device.name} ${device.brand} ${device.inventoryNumber} ${device.serialNumber}`.toLowerCase().includes(needle));
      const selected = data.devices.find(device => String(device.id) === params.get('device'));
      return <>
        <ul className="xo-device-list" aria-label={tx('ui.lab.machines.title')}>{devices.map(device => <li key={device.id}>
          <button type="button" className={`xo-device-row is-${device.status}`} onClick={() => setParams({ device: String(device.id) })}
            aria-label={`${device.name} · ${device.inventoryNumber}`}>
            <span className="xo-device-art"><DeviceArt device={device} /></span>
            <span className="xo-device-identity"><strong>{device.name}</strong><small>{device.brand}</small></span>
            <span className="xo-device-codes"><code>{device.inventoryNumber}</code><small>{text('serial_short', { serial: device.serialNumber })}</small></span>
            <span className="xo-device-dept">{device.department.code}</span>
            <span className="xo-device-count">{text('stage_count', { count: device.stages.length })}</span>
            <Pill tone={TONES[device.status]}>{text(`status.${device.status}`)}</Pill>
            <ChevronRight aria-hidden="true" />
          </button>
        </li>)}</ul>
        {devices.length === 0 && <LabEmpty />}
        {selected && <DeviceDetails device={selected} mayManage={data.mayManage} onSaved={resource.refresh}
          onClose={() => { const next = new URLSearchParams(params); next.delete('device'); setParams(next, { replace: true }); }} />}
      </>;
    }}</LabResource>
  </div>;
}
